import type { QuotationResponse } from '@palaccount/contracts';

/**
 * SAL-01 §7's guarded lifecycle, mirrored for OFFERING controls only — the
 * server (and its DB triggers) enforce every transition regardless
 * (BR-PERM-49/-50). One table, read by the editor:
 *
 *   draft    → sent
 *   sent     → accepted | rejected
 *   expired  → accepted            (R4 / BR-SAL-01-14 — expiry never refuses)
 *   rejected → (nothing — only a new revision reopens it, BR-SAL-01-02)
 *
 * Conversion (R4 / BR-SAL-01-09: NEVER blocked by expiry) is offered from
 * accepted and expired; NEW REVISION from everything but converted (a
 * converted quotation's content belongs to the document it became) and
 * DRAFT (a draft never went out — it is edited in place, BR-SAL-01-10a, and
 * there is nothing to freeze; the server refuses the same way).
 */

export type QuoteStatus = QuotationResponse['status'];

const TRANSITIONS: Record<QuoteStatus, readonly ('sent' | 'accepted' | 'rejected')[]> = {
  draft: ['sent'],
  sent: ['accepted', 'rejected'],
  accepted: [],
  rejected: [],
  expired: ['accepted'],
  converted: [],
};

/** The W1 targets the editor may offer from `from`. */
export function allowedTransitions(from: QuoteStatus): readonly ('sent' | 'accepted' | 'rejected')[] {
  return TRANSITIONS[from];
}

/** Convert to Invoice / Convert to Order availability (R4: expired converts). */
export function canConvert(status: QuoteStatus): boolean {
  return status === 'accepted' || status === 'expired';
}

/**
 * NEW REVISION availability — BR-SAL-01-16 gates it on a permission ONLY
 * among the statuses that CAN be revised: not converted, and not a draft
 * (nothing went out, nothing to freeze — edit it in place).
 */
export function canRevise(status: QuoteStatus): boolean {
  return status !== 'converted' && status !== 'draft';
}

/** The grid and header are editable exactly while draft (BR-SAL-01-10a). */
export function isEditable(status: QuoteStatus): boolean {
  return status === 'draft';
}

/** Only a v1 draft with no frozen history may be deleted (BR-SAL-01-10). */
export function canDelete(status: QuoteStatus, revisionNo: number): boolean {
  return status === 'draft' && revisionNo === 1;
}
